import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import hospitalInfo from "../constants/hospitalInfo";
import formatDate from "./formatDate";
import logoBase64 from "../constants/logoBase64";

const currency = (n) =>
    `Rs. ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function numberToWords(num) {
    if (!num || isNaN(num) || num <= 0) return "Zero Rupees Only";
    const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    const inWords = (n) => {
        let str = "";
        if (n >= 10000000) {
            str += inWords(Math.floor(n / 10000000)) + " Crore ";
            n %= 10000000;
        }
        if (n >= 100000) {
            str += inWords(Math.floor(n / 100000)) + " Lakh ";
            n %= 100000;
        }
        if (n >= 1000) {
            str += inWords(Math.floor(n / 1000)) + " Thousand ";
            n %= 1000;
        }
        if (n >= 100) {
            str += inWords(Math.floor(n / 100)) + " Hundred ";
            n %= 100;
        }
        if (n > 0) {
            if (n < 20) {
                str += a[n] + " ";
            } else {
                str += b[Math.floor(n / 10)] + (n % 10 > 0 ? " " + a[n % 10] : "") + " ";
            }
        }
        return str.trim();
    };

    const whole = Math.floor(num);
    const paise = Math.round((num - whole) * 100);
    let result = inWords(whole) + " Rupees";
    if (paise > 0) {
        result += " and " + inWords(paise) + " Paise";
    }
    return result + " Only";
}

/**
 * Downloads a formal salary slip PDF for a staff member using the official Green Naturopathy Bill Template design.
 */
export function downloadPaySlipPDF(staff = {}, monthlyData = {}, year = new Date().getFullYear(), month = new Date().getMonth() + 1) {
    if (!staff || !staff.name) return;

    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 40;

    // Palette matching the Bill Template
    const brandGreen = [75, 128, 38];      // #4b8026 Organic Leaf Green
    const grayHeaderBg = [209, 213, 219];  // #d1d5db Header gray
    const grayContentBg = [249, 250, 251]; // #f9fafb Soft card gray
    const grayBorder = [209, 213, 219];    // #d1d5db Box border
    const textDark = [31, 41, 55];         // #1f2937 Charcoal
    const textMuted = [107, 114, 128];     // #6b7280 Muted gray
    const statusGreen = [115, 167, 66];    // #73a742 Status green badge

    const monthName = new Date(year, month - 1, 1).toLocaleString("default", { month: "long" });

    // ---------- Top Header: Title on Left, Logo on Right ----------
    doc.setFont("times", "bold");
    doc.setFontSize(36);
    doc.setTextColor(...brandGreen);
    doc.text("Salary Pay Slip", marginX, 54);

    // Official Logo on top right (exact content size: 4.5cm x 2.26cm -> 127.56pt x 64.06pt)
    const logoWidth = 127.56;
    const logoHeight = 64.06;
    const logoX = pageWidth - marginX - logoWidth;
    const logoY = 18;
    if (logoBase64) {
        try {
            doc.addImage(logoBase64, "PNG", logoX, logoY, logoWidth, logoHeight);
        } catch (e) {
            console.warn("Could not render logo in PaySlip PDF", e);
        }
    }

    let y = 92;

    // ---------- Side-by-side Gray-capped cards ----------
    const cardGap = 14;
    const cardWidth = (pageWidth - (marginX * 2) - cardGap) / 2;
    const cardHeight = 84;
    const headerHeight = 17;

    const presentDays = monthlyData.summary?.present || 0;
    const halfDays = monthlyData.summary?.halfDay || 0;
    const holidays = monthlyData.summary?.holiday || 0;
    const leaves = monthlyData.summary?.absent || 0;
    const payableDays = monthlyData.payableDays || (30 - leaves - (halfDays * 0.5));

    // LEFT CARD: Employee Details
    doc.setFillColor(...grayContentBg);
    doc.setDrawColor(...grayBorder);
    doc.roundedRect(marginX, y, cardWidth, cardHeight, 3, 3, "FD");

    doc.setFillColor(...grayHeaderBg);
    doc.rect(marginX, y, cardWidth, headerHeight, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...textDark);
    doc.text("EMPLOYEE DETAILS", marginX + 10, y + 12);

    let leftTextY = y + 28;
    doc.setFontSize(9.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(17, 24, 39);
    doc.text((staff.name || "-").toUpperCase(), marginX + 10, leftTextY);

    leftTextY += 13;
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...textMuted);
    doc.text("EMPLOYEE ID:", marginX + 10, leftTextY);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...textDark);
    doc.text(staff.staffId || "-", marginX + 85, leftTextY);

    leftTextY += 12;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...textMuted);
    doc.text("DESIGNATION:", marginX + 10, leftTextY);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...textDark);
    doc.text(staff.designation || "Staff Member", marginX + 85, leftTextY);

    leftTextY += 12;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...textMuted);
    doc.text("JOINING DATE:", marginX + 10, leftTextY);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...textDark);
    doc.text(staff.joiningDate ? formatDate(staff.joiningDate) : "-", marginX + 85, leftTextY);

    // RIGHT CARD: Payroll Summary
    const rightCardX = marginX + cardWidth + cardGap;
    doc.setFillColor(...grayContentBg);
    doc.setDrawColor(...grayBorder);
    doc.roundedRect(rightCardX, y, cardWidth, cardHeight, 3, 3, "FD");

    doc.setFillColor(...grayHeaderBg);
    doc.rect(rightCardX, y, cardWidth, headerHeight, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...textDark);
    doc.text("PAYROLL SUMMARY", rightCardX + 10, y + 12);

    let rightTextY = y + 28;
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...textMuted);
    doc.text("PAY PERIOD:", rightCardX + 10, rightTextY);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(17, 24, 39);
    doc.text(`${monthName.toUpperCase()} ${year}`, rightCardX + 85, rightTextY);

    rightTextY += 13;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...textMuted);
    doc.text("ISSUE DATE:", rightCardX + 10, rightTextY);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...textDark);
    doc.text(formatDate(new Date()), rightCardX + 85, rightTextY);

    rightTextY += 12;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...textMuted);
    doc.text("PAID DAYS:", rightCardX + 10, rightTextY);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...textDark);
    doc.text(`${payableDays} / 30 Days`, rightCardX + 85, rightTextY);

    rightTextY += 12;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...textMuted);
    doc.text("PAY STATUS:", rightCardX + 10, rightTextY);

    // Status Badge
    doc.setFillColor(...statusGreen);
    doc.roundedRect(rightCardX + 85, rightTextY - 8, 55, 11, 2, 2, "F");
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("PROCESSED", rightCardX + 112.5, rightTextY, { align: "center" });

    y += cardHeight + 14;

    // ---------- Attendance Performance Box (Clean Grid) ----------
    autoTable(doc, {
        startY: y,
        margin: { left: marginX, right: marginX },
        theme: "plain",
        styles: {
            fontSize: 8,
            cellPadding: 5,
            textColor: textDark,
            lineColor: grayBorder,
            lineWidth: 0.5
        },
        headStyles: {
            fillColor: [229, 231, 235], // #e5e7eb
            textColor: textDark,
            fontStyle: "bold",
            fontSize: 8
        },
        head: [["ATTENDANCE PERFORMANCE METRICS", "", "", ""]],
        body: [
            [
                "Standard Working Base:", "30 Days",
                "Present Days:", `${presentDays} Days`
            ],
            [
                "Holidays / Weekly Off:", `${holidays} Days`,
                "Half Days (0.5):", `${halfDays} Days`
            ],
            [
                "Leaves / Absent:", `${leaves} Days`,
                "Payable / Paid Days:", `${payableDays} Days`
            ]
        ],
        columnStyles: {
            0: { fontStyle: "bold", cellWidth: 140, fillColor: grayContentBg },
            1: { cellWidth: 118 },
            2: { fontStyle: "bold", cellWidth: 140, fillColor: grayContentBg },
            3: { cellWidth: 117 }
        }
    });

    y = doc.lastAutoTable.finalY + 14;

    // ---------- Salary & Allowances Breakdown Table ----------
    const baseSalary = Number(monthlyData.baseSalary !== undefined ? monthlyData.baseSalary : (staff.monthlySalary || 0));
    const deduction = Number(monthlyData.deduction || 0);
    const allowance = Number(monthlyData.allowance || 0);
    const otherDeductions = Number(monthlyData.otherDeductions || 0);
    const totalEarnings = baseSalary + allowance;
    const totalDeductions = deduction + otherDeductions;
    const netSalary = monthlyData.netSalary !== undefined ? Number(monthlyData.netSalary) : Math.max(0, totalEarnings - totalDeductions);

    autoTable(doc, {
        startY: y,
        margin: { left: marginX, right: marginX },
        theme: "plain",
        styles: {
            fontSize: 8.5,
            cellPadding: 6,
            textColor: textDark,
            lineColor: grayBorder,
            lineWidth: 0.5
        },
        headStyles: {
            fillColor: [229, 231, 235], // #e5e7eb
            textColor: textDark,
            fontStyle: "bold",
            fontSize: 8.5
        },
        head: [["EARNINGS", "AMOUNT", "DEDUCTIONS", "AMOUNT"]],
        body: [
            ["Basic Monthly Salary", currency(baseSalary), "Leave / Absence Deduction", deduction > 0 ? currency(deduction) : "Rs. 0.00"],
            ["Special Allowances", allowance > 0 ? currency(allowance) : "Rs. 0.00", "Other Deductions / Tax", otherDeductions > 0 ? currency(otherDeductions) : "Rs. 0.00"],
            ["Total Gross Earnings", currency(totalEarnings), "Total Deductions", currency(totalDeductions)]
        ],
        columnStyles: {
            0: { cellWidth: 160 },
            1: { cellWidth: 97.5, halign: "right", fontStyle: "bold" },
            2: { cellWidth: 160 },
            3: { cellWidth: 97.5, halign: "right", fontStyle: "bold" }
        },
        foot: [
            [
                { content: "NET SALARY PAYABLE", colSpan: 2, styles: { fontStyle: "bold", fontSize: 10, textColor: brandGreen } },
                { content: currency(netSalary), colSpan: 2, styles: { fontStyle: "bold", fontSize: 11, textColor: brandGreen, halign: "right" } }
            ]
        ],
        footStyles: {
            fillColor: [240, 248, 240],
            lineColor: statusGreen,
            lineWidth: 1
        }
    });

    y = doc.lastAutoTable.finalY + 14;

    // ---------- Amount in Words & Notes Box ----------
    doc.setFillColor(...grayContentBg);
    doc.setDrawColor(...grayBorder);
    doc.roundedRect(marginX, y, pageWidth - (marginX * 2), 48, 3, 3, "FD");

    doc.setFontSize(7.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...textMuted);
    doc.text("TOTAL NET SALARY IN WORDS", marginX + 12, y + 14);

    doc.setFontSize(9.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(17, 24, 39);
    const words = numberToWords(netSalary);
    const splitWords = doc.splitTextToSize(words, pageWidth - (marginX * 2) - 24);
    doc.text(splitWords, marginX + 12, y + 28);

    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...textMuted);
    doc.text("Note: This salary slip is a confidential document issued by Green Naturopathy. For any inquiries, please contact administration.", marginX + 12, y + 41);

    y += 75;

    // ---------- Signature Block ----------
    doc.setDrawColor(...grayBorder);
    doc.line(marginX, y, marginX + 140, y);
    doc.line(pageWidth - marginX - 160, y, pageWidth - marginX, y);

    doc.setFontSize(8.5);
    doc.setTextColor(...textDark);
    doc.setFont("helvetica", "bold");
    doc.text("Employee Signature", marginX + 15, y + 14);
    doc.text("Authorized Signatory", pageWidth - marginX - 130, y + 14);

    // ---------- Bottom Hospital Address Banner (Identical to Bill Template) ----------
    const footerY = pageHeight - 38;
    doc.setDrawColor(...grayBorder);
    doc.setLineWidth(0.75);
    doc.line(marginX, footerY - 10, pageWidth - marginX, footerY - 10);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(55, 65, 81);
    const centreName = hospitalInfo.centreName || "GREEN NATUROPATHY";
    const addressLine = `${centreName}: ${(hospitalInfo.address || "194-C, SATHY ROAD, GANAPATHY, COIMBATORE")}, ${(hospitalInfo.city || "COIMBATORE")}, ${(hospitalInfo.state || "TAMIL NADU")} ${(hospitalInfo.pincode || "641006")}`;
    doc.text(addressLine.toUpperCase(), pageWidth / 2, footerY, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...textMuted);
    const phoneVal = hospitalInfo.phone ? `+91 ${hospitalInfo.phone}` : "+91 9345316319";
    const emailVal = hospitalInfo.email || "greennaturopathycbe@gmail.com";
    const webVal = hospitalInfo.websiteDisplay || "greennaturopathy.com";
    doc.text(`📞 ${phoneVal}   •   ✉️ ${emailVal}   •   🌐 ${webVal}`, pageWidth / 2, footerY + 10, { align: "center" });

    // Save
    const safeStaffName = (staff.name || "Staff").replace(/[^a-zA-Z0-9]/g, "_");
    doc.save(`Payslip_${safeStaffName}_${monthName}_${year}.pdf`);
}
